import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const randomId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const useMediasoupSignaling = (url, joinPayload) => {
  const socketRef = useRef(null);
  const queueRef = useRef([]);
  const requestsRef = useRef(new Map());
  const handlerRef = useRef(null);
  const [ready, setReady] = useState(false);

  const flushQueue = useCallback(() => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    while (queueRef.current.length) {
      const message = queueRef.current.shift();
      socketRef.current.send(JSON.stringify(message));
    }
  }, []);

  const send = useCallback((payload) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      queueRef.current.push(payload);
      return;
    }
    socketRef.current.send(JSON.stringify(payload));
  }, []);

  const request = useCallback(
    (action, data = {}) =>
      new Promise((resolve, reject) => {
        const correlationId = randomId();
        requestsRef.current.set(correlationId, { resolve, reject });
        send({ type: "request", action, data, correlationId });
      }),
    [send]
  );

  useEffect(() => {
    if (!url) {
      setReady(false);
      return () => {};
    }
    const socket = new WebSocket(url);
    socketRef.current = socket;
    socket.onopen = () => {
      setReady(true);
      if (joinPayload) {
        socket.send(JSON.stringify({ type: "join", ...joinPayload }));
      }
      flushQueue();
    };
    socket.onmessage = (event) => {
      const payload = JSON.parse(event.data);
      if (payload.type === "response" && payload.correlationId) {
        const pending = requestsRef.current.get(payload.correlationId);
        if (!pending) {
          return;
        }
        requestsRef.current.delete(payload.correlationId);
        if (payload.error) {
          pending.reject(new Error(payload.error));
        } else {
          pending.resolve(payload.data);
        }
        return;
      }
      const handler = handlerRef.current;
      if (handler) {
        handler(payload);
      }
    };
    socket.onerror = (error) => {
      requestsRef.current.forEach(({ reject }) => reject(error));
      requestsRef.current.clear();
    };
    socket.onclose = () => {
      setReady(false);
      requestsRef.current.forEach(({ reject }) => reject(new Error("socket_closed")));
      requestsRef.current.clear();
    };
    return () => {
      socket.close();
      socketRef.current = null;
      setReady(false);
    };
  }, [url, joinPayload, flushQueue]);

  const setHandler = useCallback((fn) => {
    handlerRef.current = fn;
  }, []);

  const value = useMemo(
    () => ({ ready, request, sendRaw: send, setHandler }),
    [ready, request, send, setHandler]
  );

  return value;
};

export const releaseMediaStream = (stream) => {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => {
    track.stop();
  });
};

const RecordedSound = () => null;

export default RecordedSound;
